import { useRouter } from "next/router";
import { CommonHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import { NewsCard } from "@components/NewsCard";
import Pager from "@components/Pager";
import { Text } from "@components/Text";
import { usePagination } from "@hooks/usePagination";
import { isTypeProps, queryForSSP } from "@libs/apolloClient";
import { checkTeamGenre } from "@libs/utils/teamPage";
import {
  TeamsSoccerSlugNews_TeamProfileDocument as TeamProfileDocument,
  useTeamsSoccerSlugNews_NewsArticlesQuery as useNewsArticlesQuery,
} from "./__generated__/queries";
import styles from "./index.module.css";
import type { TeamsSoccerSlugNews_TeamProfileQueryResult as TeamProfileQueryResult } from "./__generated__/queries";
import type { ApolloError } from "@apollo/client";
import type { GetServerSideProps, NextPage } from "next";

type Props = {
  data?: TeamProfileQueryResult["data"];
  error?: ApolloError;
};

const ITEMS_PER_PAGE = 10;
const TeamNewsArticles: NextPage<Props> = ({ data, error }) => {
  const { query } = useRouter();
  const teamSlug =
    query.slug && typeof query.slug === "string" ? query.slug : undefined;
  const team = data?.teamProfile.team;

  const { currentPage, navigate } = usePagination();

  const { data: newsArticlesData, loading } = useNewsArticlesQuery({
    variables: {
      page: currentPage,
      perPage: ITEMS_PER_PAGE,
      masterTeamSlug: teamSlug || "",
    },
  });
  const newsArticles = newsArticlesData?.newsArticles;

  if (error) {
    console.error(error);
  }

  const tournamentName = team?.topPriorityTournament?.name ?? "";

  return (
    <CommonLayout>
      {team && !loading && (
        <>
          <CommonHead
            title={`${team.name}関連のニュース一覧`}
            description={`${tournamentName}の${team.name}関連のニュース一覧です。スポーツ観戦がもっと楽しくなるイベント情報や、おトクなキャンペーンのお知らせをご紹介。スポーツバー探し、予約はFanstaで。`}
            ogType={"website"}
          />
          <div className={styles.teamNewsWrapper}>
            <div className={styles.teamNewsContentWidth}>
              <div className={styles.articlesListHeader}>
                <h1 className="c-contentTitle c-contentTitle--fz23">
                  {`${team.name}関連のニュース`}
                </h1>
              </div>
              {newsArticles?.nodes.length === 0 && (
                <div className={styles.notfound}>
                  <Text size={15}>ニュースはありません</Text>
                </div>
              )}
              {newsArticles && (
                <div className={styles.articlesList}>
                  {newsArticles.nodes.map((n) => (
                    <NewsCard key={n.id} newsArticle={n} />
                  ))}
                </div>
              )}
              {newsArticles && newsArticles.nodes.length > 0 && (
                <Pager
                  onNavigation={navigate}
                  hasPreviousPage={newsArticles.hasPreviousPage}
                  hasNextPage={newsArticles.hasNextPage}
                  totalPage={newsArticles.totalPage}
                  currentPage={currentPage}
                />
              )}
            </div>
          </div>
        </>
      )}
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params } = context;
  const pageProps = await queryForSSP({
    query: TeamProfileDocument,
    variables: {
      slug: params?.slug || "",
    },
  });
  if (
    !isTypeProps(pageProps) ||
    !checkTeamGenre(pageProps.props.data, params)
  ) {
    return {
      notFound: true,
    };
  }
  return pageProps;
};

export default TeamNewsArticles;
